import * as React from "react";
import { Account } from "@/types";
import { cleanedErrors } from "@/utils/Functions";
import { BooleanInput } from "@/components/atoms/BooleanInput";
import { FormField } from "@/components/molecules/FormField";
import { InfoBoxTrigger } from "@/components/molecules";
import { useOnValueChange, useUpdater } from "@/utils/callbacks";
import { PropertyStatus } from "../PropertyFields";
import {
  accountFieldsFromRecord as meterAccountFieldsFromRecord,
  Errors as MeterInfoErrors,
  errorsOf as meterInfoErrorsOf,
  MeterInfoField,
  MeterInfo as MeterInfoRecord,
  recordFromAccount as meterInfoRecordFromAccount,
} from "./WaterAccountMeterInfoFields";
import { isNWG } from "@/helpers/service-provider-helpers";

export type WaterAccountFieldsRecord = {
  responsibleAcceptsArrears?: boolean;
  meterInfo: MeterInfoRecord;
};

export type Errors =
  | undefined
  | {
      meterInfo?: MeterInfoErrors;
    };

export const errorsOfWaterAccountFields: (r: WaterAccountFieldsRecord) => Errors = (record) => {
  return cleanedErrors({
    meterInfo: meterInfoErrorsOf(record?.meterInfo),
  });
};

export const waterAccountFieldsRecordFromAccount = (
  account?: Account
): WaterAccountFieldsRecord => {
  return {
    responsibleAcceptsArrears: !account?.responsibleRejectsArrears,
    meterInfo: meterInfoRecordFromAccount(account),
  };
};

export const waterAccountFieldsFromRecord = (
  record?: WaterAccountFieldsRecord
): Partial<Account> => {
  if (!record) return {};

  return {
    responsibleRejectsArrears: !record.responsibleAcceptsArrears,
    ...meterAccountFieldsFromRecord(record.meterInfo),
  };
};

export type WaterAccountFieldsProps = {
  record: WaterAccountFieldsRecord;
  errors?: Errors;
  update: (f: (record: WaterAccountFieldsRecord) => WaterAccountFieldsRecord) => void;
  propertyStatus?: PropertyStatus;
  serviceProviderId?: string;
};

export const WaterAccountFields: React.FC<WaterAccountFieldsProps> = ({
  record,
  errors,
  update,
  propertyStatus,
  serviceProviderId,
}) => {
  const updateMeterInfo = useUpdater(update, "meterInfo");

  return (
    <>
      {propertyStatus === PropertyStatus.ExistingOccupier && (
        <ResponsibleAcceptsArrears
          record={record}
          update={update}
          serviceProviderId={serviceProviderId}
        />
      )}

      <MeterInfoField
        record={record.meterInfo}
        errors={errors?.meterInfo}
        update={updateMeterInfo}
      />
    </>
  );
};

const ResponsibleAcceptsArrears: React.FC<{
  record: WaterAccountFieldsRecord;
  update: any;
  serviceProviderId?: string;
}> = ({ record, update, serviceProviderId }) => {
  const onValueChange = useOnValueChange(update, "responsibleAcceptsArrears");

  return (
    <FormField>
      <BooleanInput
        label={
          <span>
            I confirm that the existing occupant is{" "}
            <b>willing to accept responsibility for any arrears</b> (i.e. amounts owed) that may be
            on the accounts.
            {isNWG(serviceProviderId) && <InfoBoxTrigger content={renderInfoBox()} />}
          </span>
        }
        value={record.responsibleAcceptsArrears}
        onValueChange={onValueChange}
      />
    </FormField>
  );
};

const renderInfoBox = () => {
  return (
    <div>
      <p>
        <b>Why is this necessary?</b>
      </p>

      <p>
        Please note that if arrears are not accepted by the existing occupant, the existing account
        will be closed and a new account opened in the name of the existing occupant.
      </p>

      <p style={{ marginBottom: 0 }}>
        Any amounts owed or refunds due will be recovered from or paid to the estate of the person
        who died.
      </p>
    </div>
  );
};
